import React, { FC, Fragment, useCallback, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import FileDragNDrop from 'components/FileDragNDrop'
import Button from 'UI/Button/ButtonWithPreloader'
import { ReactComponent as ArrowLeftIcon } from 'assets/svg/ArrowLeftIcon.svg'
import { useMutation, useQuery } from 'react-query'
import { getClientById, deleteFileFromClient } from 'services/clients'
import { IClientDocumentConnectParamsResponse, IUploadedClientCategoriesKeys, IUploadedClientDocuments } from 'typescript/interfaces/deals'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { SOMETHING_WENT_WRONG } from 'constants/errors'
import { removeAtIndex } from 'utils/arrayHelpers'
import { IInitClientDocsValues, IRemoveClientFileParams } from 'typescript/interfaces/clients'
import { AxiosError } from 'axios'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import ModalComponent from 'UI/Modal'
import { documentsClientValidationSchema } from 'components/ClientForms/DocumentsForm/schema'
import { getBankStatementFileName, getRollingMonthWindow } from 'utils/bankStatementHelpers'
import { useRequestDocumentVerification } from 'hooks/useDocumentVerification'
import { isVerifiedDocumentType } from 'utils/documentVerificationHelpers'
import { DocumentVerificationTrigger } from 'typescript/enums/documentVerification'

interface IDocumentForm {
  submit: {
    label: string
    handler: (values: IInitClientDocsValues) => any
  }
  loading?: boolean
  back: {
    label: string
    handler: () => any
  }
  initialValues?: IInitClientDocsValues
}

const initValues: IInitClientDocsValues = {
  documents: {
    VoidedCheck: [],
    Application: [],
    DriverLicense: [],
    BankStatements: [],
    CreditProcessingStatements: [],
    W9: [],
    Optional: [],
  },
  uploadedDocuments: {
    VoidedCheck: [],
    Application: [],
    DriverLicense: [],
    BankStatements: [],
    CreditProcessingStatements: [],
    W9: [],
    Optional: [],
  },
}

const DocumentsForm: FC<IDocumentForm> = ({ submit, back, initialValues = initValues, loading }) => {
  const [deleteFile, setDeleteFile] = useState<{
    file: IClientDocumentConnectParamsResponse | undefined
    open: boolean
  }>({
    file: undefined,
    open: false,
  })
  const { enqueueSnackbar } = useSnackbar()
  const { id } = useParams<{ id: string }>()
  const { requestVerification } = useRequestDocumentVerification()
  const { data } = useQuery(['client', id], () => getClientById(id), {
    onSuccess(res) {
      const {
        data: { documents },
      } = res

      const documentsToSet: IUploadedClientDocuments = {
        VoidedCheck: [],
        Application: [],
        DriverLicense: [],
        BankStatements: [],
        CreditProcessingStatements: [],
        W9: [],
        Optional: [],
      }

      documents.forEach((doc) => {
        documentsToSet[doc.documentType].push(doc)
      })

      setFieldValue('uploadedDocuments', documentsToSet)
    },
    onError() {
      enqueueSnackbar(SOMETHING_WENT_WRONG)
    },
    enabled: id,
  })

  const monthWindow = useMemo(() => getRollingMonthWindow(), [])

  const { handleSubmit, setFieldValue, values, touched, errors } = useFormik({
    initialValues: initialValues,
    onSubmit: submit.handler,
    validationSchema: documentsClientValidationSchema,
  })

  const bankStatements = useMemo(() => {
    if (!data?.data.bankAccounts) return undefined

    return data.data.bankAccounts.map(({ bankAccount }) => ({
      bankName: bankAccount.bankName,
      calendar: monthWindow.map(({ month, year }) => {
        const prefix = getBankStatementFileName(bankAccount, month, year)
        return {
          prefix,
          month: `${month} ${year}`,
          files: values.documents.BankStatements.filter((x) => x.name.startsWith(prefix)),
          uploadedFiles: values.uploadedDocuments.BankStatements.filter((x) => x.description.startsWith(prefix)),
        }
      }),
    }))
  }, [data?.data.bankAccounts, values.documents.BankStatements, values.uploadedDocuments.BankStatements, monthWindow])

  const uncategorizedBankStatements = useMemo(() => {
    if (!bankStatements) return undefined

    const categorized = bankStatements.flatMap((x) => x.calendar.flatMap((e) => e.uploadedFiles))

    return values.uploadedDocuments.BankStatements.filter((x) => !categorized.includes(x))
  }, [bankStatements])

  const handleDrop = useCallback(
    (files: File[], name: IUploadedClientCategoriesKeys) => {
      let filesToAdd: File[] = []
      // if (files.length > 0 && !files.some((f) => f.name.endsWith('.pdf'))) {
      //   enqueueSnackbar("Files with inappropriate format can't be added")
      //   filesToAdd = files.filter((f) => f.name.endsWith('.pdf'))
      // } else
      if (files.length > 0 && files.some((f) => f.size > 500000000)) {
        enqueueSnackbar('Files must be no more than 500MB.')
        filesToAdd = files.filter((f) => f.size <= 500000000)
      } else {
        filesToAdd = files
      }
      setFieldValue(`documents.${name}`, [...values.documents[name], ...filesToAdd])
    },
    [values],
  )

  const handleDropBankStatement = (files: File[], prefix: string) => {
    const renamedFiles = files.map((x) => new File([x], `${prefix} - ${x.name}`, { type: x.type }))
    handleDrop(renamedFiles, 'BankStatements')
  }

  /** Remove local file **/
  const handleRemove = useCallback(
    (index: number, name: IUploadedClientCategoriesKeys) => {
      setFieldValue(`documents.${name}`, removeAtIndex(values.documents[name], index))
    },
    [values],
  )

  /** Remove file from S3 storage **/
  const [mutateRemove, { isLoading: isLoadingDeleteFile }] = useMutation(
    ({ id: docId, name }: IRemoveClientFileParams) => deleteFileFromClient(docId),
    {
      onSuccess(_, variables) {
        enqueueSnackbar(<Typography>Successfully deleted document</Typography>)
        if (isVerifiedDocumentType(variables.name)) {
          requestVerification({ clientId: id, trigger: DocumentVerificationTrigger.documentDeleted })
        }
        setFieldValue(
          `uploadedDocuments.${variables.name}`,
          values.uploadedDocuments[variables.name].filter((doc) => `${doc.id}` !== variables.id),
        )
        setDeleteFile({
          file: undefined,
          open: false,
        })
      },
      onError: (err: AxiosError) => {
        setDeleteFile({
          file: undefined,
          open: false,
        })
        if (err.response && err.response.data.description === 'CANT_DELETE_LAST_DOCUMENT')
          enqueueSnackbar('The last file of this type cannot be deleted')
      },
    },
  )

  const handleRemoveUploaded = useCallback(
    (documentId: number, name: IUploadedClientCategoriesKeys) => {
      const deletedFile = values.uploadedDocuments[name].find((doc) => doc.id === documentId)
      setDeleteFile({
        file: deletedFile,
        open: true,
      })
    },
    [setFieldValue, mutateRemove, values],
  )

  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <Typography variant="h3" color="textSecondary">
          Required Documents
        </Typography>
      </Box>
      <Box mt="1.5rem">
        <Grid container spacing={4}>
          <Grid xs={12} sm={6} lg={3} item>
            <FileDragNDrop
              title="Voided check"
              name="VoidedCheck"
              onDrop={handleDrop}
              uploadedFiles={values.uploadedDocuments.VoidedCheck}
              uploaded={Boolean(values.uploadedDocuments.VoidedCheck.length)}
              files={values.documents.VoidedCheck}
              onRemove={handleRemove}
              onRemoveUploaded={handleRemoveUploaded}
              error={Boolean(touched.documents?.VoidedCheck && errors.documents?.VoidedCheck)}
            />
          </Grid>
          <Grid xs={12} sm={6} lg={3} item>
            <FileDragNDrop
              title="Application"
              name="Application"
              onDrop={handleDrop}
              uploadedFiles={values.uploadedDocuments.Application}
              uploaded={Boolean(values.uploadedDocuments.Application.length)}
              files={values.documents.Application}
              onRemove={handleRemove}
              onRemoveUploaded={handleRemoveUploaded}
              error={Boolean(touched.documents?.Application && errors.documents?.Application)}
            />
          </Grid>
          <Grid xs={12} sm={6} lg={3} item>
            <FileDragNDrop
              title="Driver's License"
              name="DriverLicense"
              onDrop={handleDrop}
              uploadedFiles={values.uploadedDocuments.DriverLicense}
              uploaded={Boolean(values.uploadedDocuments.DriverLicense.length)}
              files={values.documents.DriverLicense}
              onRemove={handleRemove}
              onRemoveUploaded={handleRemoveUploaded}
              error={Boolean(touched.documents?.DriverLicense && errors.documents?.DriverLicense)}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mt="3rem">
        <Typography variant="h3" color="textSecondary">
          Optional Documents
        </Typography>
      </Box>
      <Box mt="1.5rem">
        <Grid container spacing={4}>
          <Grid xs={12} sm={6} lg={3} item>
            <FileDragNDrop
              title="Optional"
              name="Optional"
              onDrop={handleDrop}
              uploadedFiles={values.uploadedDocuments.Optional}
              uploaded={Boolean(values.uploadedDocuments.Optional.length)}
              files={values.documents.Optional}
              onRemove={handleRemove}
              onRemoveUploaded={handleRemoveUploaded}
            />
          </Grid>
          <Grid xs={12} sm={6} lg={3} item>
            <FileDragNDrop
              title="W9"
              name="W9"
              onDrop={handleDrop}
              uploadedFiles={values.uploadedDocuments.W9}
              uploaded={Boolean(values.uploadedDocuments.W9.length)}
              files={values.documents.W9}
              onRemove={handleRemove}
              onRemoveUploaded={handleRemoveUploaded}
              error={Boolean(touched.documents?.W9 && errors.documents?.W9)}
            />
          </Grid>
          {!!uncategorizedBankStatements?.length && (
            <Grid xs={12} sm={6} lg={3} item>
              <FileDragNDrop
                disabled
                uploaded
                title="Bank Statements"
                name="BankStatements"
                uploadedFiles={uncategorizedBankStatements}
                onRemoveUploaded={handleRemoveUploaded}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {bankStatements &&
        bankStatements.map(({ bankName, calendar }) => (
          <Fragment key={bankName}>
            <Box mt="3rem">
              <Typography variant="h3" color="textSecondary">
                Bank Statements {bankName}
              </Typography>
            </Box>
            <Box mt="1.5rem">
              <Grid container spacing={4}>
                {calendar.map(({ prefix, month, files, uploadedFiles }) => {
                  return (
                    <Grid lg={2} xs={6} sm={4} item key={month}>
                      <FileDragNDrop
                        maxFiles={1}
                        disabled={files.length + uploadedFiles.length >= 1}
                        title={month}
                        name="BankStatements"
                        onDrop={(x) => handleDropBankStatement(x, prefix)}
                        uploadedFiles={uploadedFiles}
                        uploaded={Boolean(uploadedFiles.length)}
                        files={files}
                        onRemove={(i, n) => handleRemove(values.documents.BankStatements.indexOf(files[i]), n)}
                        onRemoveUploaded={handleRemoveUploaded}
                        error={Boolean(touched.documents?.BankStatements && errors.documents?.BankStatements?.includes(prefix))}
                      />
                    </Grid>
                  )
                })}
              </Grid>
            </Box>
          </Fragment>
        ))}
      <Box mt="3rem" display="flex" alignItems="center">
        <Box mr={5}>
          <Button color="secondary" onClick={() => back.handler()} variant="contained" startIcon={<ArrowLeftIcon />}>
            {back.label}
          </Button>
        </Box>
        <Button loading={loading} color="primary" variant="contained" type="submit">
          {submit.label}
        </Button>
      </Box>
      <ModalComponent open={deleteFile.open}>
        <ConfirmationContent
          isLoading={isLoadingDeleteFile}
          text="Are you sure you want to delete file?"
          handleCancel={() =>
            setDeleteFile({
              file: undefined,
              open: false,
            })
          }
          handleConfirm={() => {
            mutateRemove({
              id: deleteFile.file?.id.toString() || '',
              name: deleteFile.file?.documentType || 'VoidedCheck',
            })
          }}
        />
      </ModalComponent>
    </form>
  )
}

export default DocumentsForm
